import type { CommerceBlogPost, Breadcrumb, Category, CollectionResult, FacetFilter, Params, Product, ProductVariant, CollectionLink } from '@agapi/mlc-core';
import type { DistinctFacet, Entity, ProductVariantFlat, RangeFacet, Response, BlogPostFlat, CategoryFlat } from './loop54/types';
import type { Locale } from '@agapi/mlc-core/src/types/general';

const safeParseJson = (json: string) => {
  try {
    return JSON.parse(json);
  } catch (e) {
    return undefined;
  }
}

type ObjectWithStringKey<T, K extends string> = T & { [P in K]: string };

interface Tree {
  [key: string]: Tree;
}

export const getLeaves = <T, K extends string>(objects: ObjectWithStringKey<T, K>[], key: K, delimiter = '/'): ObjectWithStringKey<T, K>[] => {
  const tree: Tree = {
    nodes: {}
  };
  const refs: Record<string, ObjectWithStringKey<T, K>> = {};

  // Build the tree
  for (const obj of objects) {
    const path = obj[key].split(delimiter).map(p => p.trim()).join(delimiter);

    path.split(delimiter).reduce<Tree>((acc, path) => {
      if (!acc?.nodes[path]) {
        acc.nodes[path] = {
          parent: acc,
          nodes: {}
        };
      }
      return acc.nodes[path];
    }, tree);

    refs[path] = obj;
  }

  const leafs: string[] = [];

  // Helper function to check if node is a leaf
  const isLeaf = (node: Tree): boolean => {
    return Object.keys(node.nodes).length === 0;
  }

  const isMultiBranch = (node: Tree): boolean => {
    return Object.keys(node.nodes).length > 1;
  }

  const isRootBranch = (node: Tree): boolean => {
    return node.parent === undefined;
  }

  // Helper function to process nodes and find leaves
  const processNode = (node: Tree, currentPath: string[] = []): void => {

    if (isLeaf(node)) { // Pure leaf
      leafs.push(currentPath.join(delimiter));
      return;
    } else if (isMultiBranch(node) && !isRootBranch(node)) { // Multi branch that is not the root branch
      leafs.push(currentPath.join(delimiter));
      // ... continue processing the branch
    }

    for (const [key, value] of Object.entries(node.nodes)) {
      processNode(value, [...currentPath, key]);
    }
  }

  processNode(tree);

  // console.log('leafs', leafs);

  return leafs.map(leaf => refs[leaf]).filter(c => c);
}

const parseBreadcrumbs = (breadcrumbs?: string): Breadcrumb[] => {
  const crumbs = (breadcrumbs?.trim() === '{}' ? [] : JSON.parse(breadcrumbs?.trim() ?? '[]')) as {
    id: string;
    slug: string;
    title: string;
    full_slug: string;
    full_title: string;
  }[];

  return crumbs.map<Breadcrumb>((crumb) => ({
    id: crumb.id,
    slug: crumb.slug,
    title: crumb.title,
    fullSlug: crumb.full_slug,
    fullTitle: crumb.full_title
  }));
};

const parseCollectionLinks = (product: ProductVariantFlat): CollectionLink[] => {
  if (product.content_json) {
    const content = JSON.parse(product.content_json) as {
      id: number;
      slug: string;
      title: string;
      full_slug: string;
      full_title: string
    }[];

    const collections = content
      .map<CollectionLink>((c) => {
        return {
          id: c.id.toString(),
          slug: c.slug,
          title: c.title,
          fullSlug: c.full_slug.replace(/^\/(glimja|happygreen)/, ''),
          fullTitle: c.full_title
        }
      })
      .filter(c => c && c?.title && c?.fullSlug);

    return getLeaves(collections, 'fullSlug');
  }
  return [];
}

export const transformItem = (entity: Entity): ProductVariantFlat => {
  return entity.attributes?.reduce<Record<string, any>>((obj, attr) => {
    obj[attr.name] = attr.values.length > 1 ? attr.values : attr.values[0];
    return obj;
  }, {}) as ProductVariantFlat;
};

export const transformItems = (entities: Entity[]) => {
  return entities.map((e) => transformItem(e));
};

export const transformFaucets = (faucets: Array<DistinctFacet | RangeFacet>): FacetFilter[] => {
  return faucets.map((f) => {
    if (f.type === 'distinct') {
      return {
        label: f.name,
        type: f.type,
        items: getLeaves(f.items.map((i) => ({
          label: i.item as string,
          count: i.count,
          selected: i.selected
        })), 'label', '>'),
        count: f.items.length
      };
    }

    return {
      label: f.name,
      type: f.type,
      item: {
        min: f.min as number,
        max: f.max as number,
        selectedMin: f.selectedMin as number,
        selectedMax: f.selectedMax as number
      }
    };
  });
};

export const getLocalizedValue = <T>(key: string, obj: Record<string, any>, locale: Locale): T => {
  // console.log(`${key}_${locale.country}`, obj);
  return obj[`${key}_${locale.country}`] as T;
};

const transformBrand = (product: {brand_name?: string, brand_slug?: string}) => {
  const name = product.brand_name?.split('|').pop()?.trim() || product.brand_name || '';
  const handle = product.brand_slug?.split('|').pop()?.trim() || product.brand_slug || '';
  return {
    name,
    handle,
  };
};

export const transformProductVariant = (product: ProductVariantFlat, locale: Locale): ProductVariant => {
  return {
    id: product.id,
    name: product.name,
    sku: product.sku,
    handle: encodeURIComponent(product.handle),
    description: {
      long: product.long_description,
      short: product.short_description
    },
    breadcrumbs: parseBreadcrumbs(product.breadcrumb),
    categories: product.categories,
    stock: product.stock,
    images: product.images ? JSON.parse(product.images) : [],
    brand: transformBrand(product),
    price: {
      amount: getLocalizedValue('price_base', product, locale) ?? 0,
      discount: getLocalizedValue('price_discount', product, locale) ?? 0,
      vat: getLocalizedValue('price_vat', product, locale) ?? 0,
      currency: getLocalizedValue('currency', product, locale)
    },
    collections: parseCollectionLinks(product),
    meta: {
      canonical: product.canonical,
      title: product.meta_title,
      description: product.meta_description
    },
    relatedBlogPosts: product.related_blog_posts?.replace(/\|/g, ','),
    languageSlugs: product.language_slugs ? safeParseJson(product.language_slugs) : undefined,
    languageFullSlugs: product.language_slugs ? transformLanguageFullSlugs(safeParseJson(product.language_slugs)) : undefined
  }
};

export const transformLanguageFullSlugs = (languageSlugs?: Record<string, string>) => {
  if (!languageSlugs) return undefined;

  const pathMappings = {
    sv: '/produkt/',
    en: '/en/product/',
    fi: '/fi/tuote/',
    nb: '/nb/produkt/',
    da: '/da/produkt/',
    es: '/es/producto/'
  };

  const result: Record<string, string> = {};

  for (const [lang, path] of Object.entries(pathMappings)) {
    if (languageSlugs[lang]) {
      result[lang] = `${path}${languageSlugs[lang]}`;
    }
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

export const transformProduct = (product: ProductVariantFlat, locale: Locale): Product => {
  return {
    id: product.id,
    brand: transformBrand(product),
    description: {
      long: product.long_description,
      short: product.short_description
    },
    breadcrumbs: parseBreadcrumbs(product.breadcrumb),
    categories: product.categories,
    handle: encodeURIComponent(product.handle),
    name: product.name,
    ingredients: product.ingredients,
    nutrition: product['nutritional-content'],
    variants: [ transformProductVariant(product, locale) ],
    collections: parseCollectionLinks(product),
    meta: {
      canonical: product.canonical,
      title: product.meta_title,
      description: product.meta_description
    },
    // TODO: Replace with related_blog_posts instead of related_blog_posts_GL
    relatedBlogPosts: product.related_blog_posts?.replace(/\|/g, ','),
    languageSlugs: product.language_slugs ? safeParseJson(product.language_slugs) : undefined,
    languageFullSlugs: product.language_slugs ? transformLanguageFullSlugs(safeParseJson(product.language_slugs)) : undefined
  };
};

export const transformProducts = (products: ProductVariantFlat[], locale: Locale): Product[] => {
  return products.map((p) => transformProduct(p, locale));
};

export const transformProductVariants = (products: ProductVariantFlat[], locale: Locale): ProductVariant[] => {
  return products.filter(p => p.id !== undefined).map((p) => transformProductVariant(p, locale));
};

export const transformCollectionResult = <T, CUSTOM_DATA>(response: Response<CUSTOM_DATA>, transformer: (response: Response<CUSTOM_DATA>) => T[], params: Params): CollectionResult<T> => {
  return {
    items: transformer(response),
    facetFilters: transformFaucets(response.results.facets),
    count: response.results.count,
    limit: params.limit,
    offset: params.offset ?? 0,
    sortBy: params.sortBy
  };
};

export const transformBlogPostFlat = (blogPost: BlogPostFlat): CommerceBlogPost => {
  const getAttribute = (name: string) => {
    const attr = blogPost.attributes.find(a => a.name === `blog_${name}`);
    return attr?.values[0];
  };

  const getAttributes = (name: string) => {
    const attr = blogPost.attributes.find(a => a.name === `blog_${name}`);
    return attr?.values ?? [];
  };

  return {
    id: blogPost.id,
    slug: getAttribute('slug') ?? '',
    title: getAttribute('title') ?? '',
    content: getAttribute('content') ?? '',
    categories: getAttributes('categoryname'),
    featuredImageUrl: getAttribute('featuredImageUrl') ?? '',
    publishedAt: getAttribute('publishedAt') ?? ''
  };
};


export const transformBlogPosts = (blogPosts: BlogPostFlat[]): CommerceBlogPost[] => {
  return blogPosts.map((p) => transformBlogPostFlat(p));
};

export const transformCategory = (category: CategoryFlat): Category => {
  const getAttribute = (name: string) => {
    const attr = category.attributes.find(a => a.name === `category_${name}`);
    return attr?.values[0];
  };

  return {
    id: category.id,
    name: getAttribute('name') ?? '',
    path: getAttribute('path') ?? '',
    description: getAttribute('description') ?? ''
  };
};


export const transformCategories = (categories: CategoryFlat[]): Category[] => {
  return categories.map((p) => transformCategory(p));
};

export const transformBrandCollection = (brand: CategoryFlat): Category => {
  const getAttribute = (name: string) => {
    const attr = brand.attributes.find(a => a.name === `brand_${name}`);
    return attr?.values[0];
  };

  return {
    id: brand.id,
    name: getAttribute('name') ?? '',
    path: getAttribute('path') ?? '',
    description: getAttribute('description') ?? ''
  };
};

export const transformBrands = (brands: CategoryFlat[]): Category[] => {
  return brands.map((p) => transformBrandCollection(p));
};